import { useEffect, useState, type FormEvent } from "react";
import type { Tenant } from "../../shared/types";
import { Boton } from "../components/Boton";
import { Campo, Input } from "../components/Formulario";
import { Insignia } from "../components/Insignia";
import { Modal } from "../components/Modal";
import { Tabla, type ColumnaTabla } from "../components/Tabla";
import { useToast } from "../components/Toast";
import { api, ErrorApi } from "../lib/apiClient";

interface TenantConMetricas extends Tenant {
  cantidad_usuarios: number;
  cantidad_expedientes: number;
}

const VALORES_ALTA_INICIALES = {
  nombre: "",
  slug: "",
  nombre_comercial: "",
  cuit: "",
  email_contacto: "",
  telefono: "",
  direccion: "",
  admin_nombre_completo: "",
  admin_email: "",
  admin_password: "",
};

export function SuperadminTenantsPage() {
  const { mostrar } = useToast();
  const [items, setItems] = useState<TenantConMetricas[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAltaAbierto, setModalAltaAbierto] = useState(false);
  const [tenantEnEdicion, setTenantEnEdicion] = useState<Tenant | null>(null);
  const [valoresAlta, setValoresAlta] = useState(VALORES_ALTA_INICIALES);
  const [guardandoAlta, setGuardandoAlta] = useState(false);
  const [valoresEdicion, setValoresEdicion] = useState({
    nombre: "",
    nombre_comercial: "",
    cuit: "",
    email_contacto: "",
    telefono: "",
    direccion: "",
  });
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  async function cargar() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: TenantConMetricas[] }>("/superadmin/tenants");
      setItems(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar los estudios.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function crearEstudio(e: FormEvent) {
    e.preventDefault();
    setGuardandoAlta(true);
    try {
      await api.post("/superadmin/tenants", {
        nombre: valoresAlta.nombre,
        slug: valoresAlta.slug,
        nombre_comercial: valoresAlta.nombre_comercial || null,
        cuit: valoresAlta.cuit || null,
        email_contacto: valoresAlta.email_contacto || null,
        telefono: valoresAlta.telefono || null,
        direccion: valoresAlta.direccion || null,
        administrador: {
          nombre_completo: valoresAlta.admin_nombre_completo,
          email: valoresAlta.admin_email,
          password: valoresAlta.admin_password,
        },
      });
      mostrar("Estudio creado.", "exito");
      setModalAltaAbierto(false);
      setValoresAlta(VALORES_ALTA_INICIALES);
      await cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo crear el estudio.", "error");
    } finally {
      setGuardandoAlta(false);
    }
  }

  function abrirEdicion(tenant: Tenant) {
    setTenantEnEdicion(tenant);
    setValoresEdicion({
      nombre: tenant.nombre,
      nombre_comercial: tenant.nombre_comercial ?? "",
      cuit: tenant.cuit ?? "",
      email_contacto: tenant.email_contacto ?? "",
      telefono: tenant.telefono ?? "",
      direccion: tenant.direccion ?? "",
    });
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!tenantEnEdicion) return;
    setGuardandoEdicion(true);
    try {
      await api.put(`/superadmin/tenants/${tenantEnEdicion.id}`, {
        nombre: valoresEdicion.nombre,
        nombre_comercial: valoresEdicion.nombre_comercial || null,
        cuit: valoresEdicion.cuit || null,
        email_contacto: valoresEdicion.email_contacto || null,
        telefono: valoresEdicion.telefono || null,
        direccion: valoresEdicion.direccion || null,
      });
      mostrar("Estudio actualizado.", "exito");
      setTenantEnEdicion(null);
      await cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el estudio.", "error");
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function cambiarEstado(tenant: TenantConMetricas) {
    const nuevoEstado = tenant.estado === "Activo" ? "Suspendido" : "Activo";
    try {
      await api.patch(`/superadmin/tenants/${tenant.id}/estado`, { estado: nuevoEstado });
      mostrar(
        nuevoEstado === "Suspendido" ? "Estudio suspendido." : "Estudio reactivado.",
        "exito",
      );
      await cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnas: ColumnaTabla<TenantConMetricas>[] = [
    {
      encabezado: "Estudio",
      render: (t) => (
        <div>
          <div className="font-medium text-slate-900">{t.nombre}</div>
          <div className="text-xs text-slate-400">/acceso/{t.slug}</div>
        </div>
      ),
    },
    { encabezado: "Estado", render: (t) => <Insignia texto={t.estado} /> },
    { encabezado: "Plan", render: (t) => t.plan },
    { encabezado: "Usuarios", render: (t) => t.cantidad_usuarios },
    { encabezado: "Expedientes", render: (t) => t.cantidad_expedientes },
    { encabezado: "Alta", render: (t) => t.creado_en.substring(0, 10) },
    {
      encabezado: "Acciones",
      render: (t) => (
        <div className="flex gap-3">
          <button
            onClick={() => abrirEdicion(t)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            Editar
          </button>
          <button
            onClick={() => cambiarEstado(t)}
            className={
              t.estado === "Activo"
                ? "font-medium text-red-600 hover:text-red-700"
                : "font-medium text-emerald-600 hover:text-emerald-700"
            }
          >
            {t.estado === "Activo" ? "Suspender" : "Reactivar"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Estudios</h1>
          <p className="mt-1 text-sm text-slate-400">
            Alta, edición y estado de cada estudio jurídico de la plataforma.
          </p>
        </div>
        <Boton onClick={() => setModalAltaAbierto(true)}>+ Nuevo Estudio</Boton>
      </div>

      <Tabla
        columnas={columnas}
        filas={items}
        claveFila={(t) => t.id}
        cargando={cargando}
        tituloVacio="Sin estudios todavía"
        descripcionVacio="Creá el primero con el botón de arriba."
      />

      <Modal
        titulo="Nuevo estudio"
        abierto={modalAltaAbierto}
        onCerrar={() => setModalAltaAbierto(false)}
        ancho="max-w-2xl"
      >
        <form onSubmit={crearEstudio} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Nombre del estudio" obligatorio>
              <Input
                required
                value={valoresAlta.nombre}
                onChange={(e) => setValoresAlta((v) => ({ ...v, nombre: e.target.value }))}
                placeholder="Estudio Villegas & Asociados"
              />
            </Campo>
            <Campo etiqueta="Identificador (slug)" obligatorio>
              <Input
                required
                pattern="[a-z0-9]+(-[a-z0-9]+)*"
                title="Minúsculas, números y guiones"
                value={valoresAlta.slug}
                onChange={(e) => setValoresAlta((v) => ({ ...v, slug: e.target.value }))}
                placeholder="villegas-asociados"
              />
            </Campo>
            <Campo etiqueta="Nombre comercial">
              <Input
                value={valoresAlta.nombre_comercial}
                onChange={(e) =>
                  setValoresAlta((v) => ({ ...v, nombre_comercial: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="CUIT">
              <Input
                value={valoresAlta.cuit}
                onChange={(e) => setValoresAlta((v) => ({ ...v, cuit: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Email de contacto">
              <Input
                type="email"
                value={valoresAlta.email_contacto}
                onChange={(e) =>
                  setValoresAlta((v) => ({ ...v, email_contacto: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="Teléfono">
              <Input
                value={valoresAlta.telefono}
                onChange={(e) => setValoresAlta((v) => ({ ...v, telefono: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Dirección">
              <Input
                value={valoresAlta.direccion}
                onChange={(e) => setValoresAlta((v) => ({ ...v, direccion: e.target.value }))}
              />
            </Campo>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">
              Primer administrador del estudio
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <Campo etiqueta="Nombre completo" obligatorio>
                <Input
                  required
                  value={valoresAlta.admin_nombre_completo}
                  onChange={(e) =>
                    setValoresAlta((v) => ({ ...v, admin_nombre_completo: e.target.value }))
                  }
                />
              </Campo>
              <Campo etiqueta="Email" obligatorio>
                <Input
                  type="email"
                  required
                  autoComplete="off"
                  value={valoresAlta.admin_email}
                  onChange={(e) => setValoresAlta((v) => ({ ...v, admin_email: e.target.value }))}
                />
              </Campo>
              <Campo etiqueta="Contraseña" obligatorio>
                <Input
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={valoresAlta.admin_password}
                  onChange={(e) =>
                    setValoresAlta((v) => ({ ...v, admin_password: e.target.value }))
                  }
                />
              </Campo>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAltaAbierto(false)}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoAlta}>
              Crear estudio
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Editar estudio"
        abierto={Boolean(tenantEnEdicion)}
        onCerrar={() => setTenantEnEdicion(null)}
        ancho="max-w-xl"
      >
        <form onSubmit={guardarEdicion} className="space-y-4">
          <Campo etiqueta="Nombre del estudio" obligatorio>
            <Input
              required
              value={valoresEdicion.nombre}
              onChange={(e) => setValoresEdicion((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Nombre comercial">
            <Input
              value={valoresEdicion.nombre_comercial}
              onChange={(e) =>
                setValoresEdicion((v) => ({ ...v, nombre_comercial: e.target.value }))
              }
            />
          </Campo>
          <Campo etiqueta="CUIT">
            <Input
              value={valoresEdicion.cuit}
              onChange={(e) => setValoresEdicion((v) => ({ ...v, cuit: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Email de contacto">
            <Input
              type="email"
              value={valoresEdicion.email_contacto}
              onChange={(e) =>
                setValoresEdicion((v) => ({ ...v, email_contacto: e.target.value }))
              }
            />
          </Campo>
          <Campo etiqueta="Teléfono">
            <Input
              value={valoresEdicion.telefono}
              onChange={(e) => setValoresEdicion((v) => ({ ...v, telefono: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Dirección">
            <Input
              value={valoresEdicion.direccion}
              onChange={(e) => setValoresEdicion((v) => ({ ...v, direccion: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setTenantEnEdicion(null)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoEdicion}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
